import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useRef, useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, Modal, QtyInput, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type {
  StockCountApplyResult,
  StockCountLine,
  StockCountReadResult,
  StockCountView,
} from "@cx/contracts";
import { formatQty } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";
import { useSession } from "../../session";
import { COUNT_STATUS_LABELS, COUNT_STATUS_TONES, scanEntry, signedQty } from "./labels";

/**
 * Conferência de estoque (RF-SUP-06): campo de leitura sempre com foco (o leitor de código de barras digita e manda
 * Enter), "12*código" lê várias de uma vez; lista com contado × saldo e a diferença; reiniciar (tudo ou um produto),
 * aplicar como balanço (com a opção de zerar o que não foi lido) e cancelar.
 */
export function StockCountPage(): ReactElement {
  const { id } = useParams({ from: "/suprimentos/conferencias/$id" });
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canEdit = can(perms, "stock", "create");
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["stock-counts", companyId, "view", id];
  const count = useQuery({
    queryKey: key,
    refetchOnMount: "always",
    queryFn: () => api<StockCountView>(`/stock/counts/${id}`),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["stock-counts", companyId] });
  const [code, setCode] = useState("");
  const [qtyQ4, setQty] = useState<number | null>(10_000);
  const [last, setLast] = useState<{ ok: boolean; text: string } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const read = useMutation({
    mutationFn: (e: { code: string; qtyQ4: number }) =>
      api<StockCountReadResult>(`/stock/counts/${id}/reads`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify(e),
      }),
    onSuccess: (r, e) => {
      setLast({
        ok: true,
        text: `+${formatQty(e.qtyQ4)} ${r.product.name} · contado ${formatQty(r.countedQ4)} ${r.product.unit}`,
      });
      refresh();
    },
    onError: (err: Error, e) => setLast({ ok: false, text: `${e.code}: ${errorMessage(err)}` }),
    onSettled: () => input.current?.focus(),
  });
  const reset = useMutation({
    mutationFn: (productId: string | null) =>
      api<{ voided: number }>(`/stock/counts/${id}/reset`, {
        method: "POST",
        body: JSON.stringify({ productId }),
      }),
    onSuccess: (r) => {
      toast("success", r.voided === 1 ? "1 leitura anulada" : `${r.voided} leituras anuladas`);
      refresh();
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const cancel = useMutation({
    mutationFn: () => api(`/stock/counts/${id}/cancel`, { method: "POST" }),
    onSuccess: () => {
      toast("success", "Conferência cancelada");
      refresh();
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const [resetting, setResetting] = useState<StockCountLine | "all" | null>(null);
  const [canceling, setCanceling] = useState(false);
  const [applying, setApplying] = useState(false);

  const c = count.data;
  const open = c?.status === "open";
  const submit = () => {
    const e = scanEntry(code, qtyQ4);
    if (!e) {
      setLast({ ok: false, text: "Leitura inválida: informe o código (ou quantidade*código)." });
      return;
    }
    setCode("");
    setQty(10_000);
    read.mutate(e);
  };
  return (
    <>
      <ContentHeader
        title={c ? `Conferência nº ${c.number} · ${c.warehouse.name}` : "Conferência de estoque"}
        breadcrumb={["Suprimentos", "Conferência de estoque", c ? String(c.number) : ""]}
        actions={
          c && (
            <>
              <Link to="/suprimentos/conferencias" className="btn btn-outline-secondary">
                Voltar
              </Link>
              {canEdit && open && (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={c.summary.readProducts === 0 && !c.summary.unreadProducts}
                    onClick={() => setApplying(true)}
                  >
                    Aplicar no estoque
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={c.summary.readProducts === 0}
                    onClick={() => setResetting("all")}
                  >
                    Reiniciar contagem
                  </button>
                  <button type="button" className="btn btn-outline-danger" onClick={() => setCanceling(true)}>
                    Cancelar conferência
                  </button>
                </>
              )}
            </>
          )
        }
      />
      <QueryStatus loading={count.isLoading} error={count.error} />
      {c && (
        <div className="row">
          <div className="col-xl-8">
            {open && canEdit && (
              <form
                className="box card p-3 mb-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  submit();
                }}
              >
                <div className="row g-2 align-items-end">
                  <div className="col-md-8">
                    <label className="form-label" htmlFor="count-code">
                      Código de barras ou SKU
                    </label>
                    <input
                      ref={input}
                      id="count-code"
                      className="form-control form-control-lg"
                      autoComplete="off"
                      autoFocus
                      inputMode="text"
                      placeholder="Leia o código (12*código para 12 unidades)"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                    />
                  </div>
                  <div className="col-6 col-md-2">
                    <label className="form-label" htmlFor="count-qty">
                      Quantidade
                    </label>
                    <QtyInput id="count-qty" value={qtyQ4} onChange={setQty} />
                  </div>
                  <div className="col-6 col-md-2">
                    <button type="submit" className="btn btn-primary btn-lg w-100" disabled={read.isPending}>
                      Ler
                    </button>
                  </div>
                </div>
                {last && (
                  <div
                    className={`alert ${last.ok ? "alert-success" : "alert-danger"} py-2 mt-3 mb-0`}
                    role={last.ok ? "status" : "alert"}
                    data-testid="count-last-read"
                  >
                    {last.text}
                  </div>
                )}
              </form>
            )}
            <div className="box card mb-3">
              <div
                className="table-responsive"
                tabIndex={0}
                role="region"
                aria-label="Produtos conferidos (rolagem)"
              >
                <table className="table align-middle mb-0" aria-label="Produtos conferidos">
                  <thead>
                    <tr>
                      <th scope="col">Produto</th>
                      <th scope="col">Código</th>
                      <th scope="col" className="text-end">
                        Contado
                      </th>
                      <th scope="col" className="text-end">
                        {open ? "Saldo atual" : "Saldo antes"}
                      </th>
                      <th scope="col" className="text-end">
                        Diferença
                      </th>
                      {open && canEdit && (
                        <th scope="col">
                          <span className="visually-hidden">Ações</span>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {c.lines.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-3">
                          {open ? "Nenhuma leitura ainda." : "Nenhum produto ajustado."}
                        </td>
                      </tr>
                    )}
                    {c.lines.map((l) => (
                      <tr key={l.productId}>
                        <td>{l.name}</td>
                        <td className="small text-muted">{l.gtin ?? l.sku ?? "—"}</td>
                        <td className="text-end qty">
                          {formatQty(l.countedQ4)} {l.unit}
                        </td>
                        <td className="text-end qty">{formatQty(l.expectedQ4)}</td>
                        <td className="text-end qty fw-semibold">{signedQty(l.deltaQ4)}</td>
                        {open && canEdit && (
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              aria-label={`Recontar ${l.name}`}
                              onClick={() => setResetting(l)}
                            >
                              Recontar
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <aside className="col-xl-4">
            <div className="box card p-3 mb-3">
              <div className="mb-2">
                <StatusBadge tone={COUNT_STATUS_TONES[c.status]}>{COUNT_STATUS_LABELS[c.status]}</StatusBadge>
              </div>
              <dl className="row mb-0 small" data-testid="count-summary">
                <dt className="col-8 fw-normal">Produtos lidos</dt>
                <dd className="col-4 text-end mb-1">{c.summary.readProducts}</dd>
                <dt className="col-8 fw-normal">Quantidade lida</dt>
                <dd className="col-4 text-end mb-1">{formatQty(c.summary.readQ4)}</dd>
                <dt className="col-8 fw-normal">Com diferença</dt>
                <dd className="col-4 text-end mb-1">{c.summary.divergent}</dd>
                <dt className="col-8 fw-normal">Com saldo e não lidos</dt>
                <dd className="col-4 text-end mb-0">{c.summary.unreadProducts}</dd>
              </dl>
              <p className="small text-muted mt-2 mb-0">
                Aberta em {dateTime(c.createdAt)}
                {c.closedAt && ` · encerrada em ${dateTime(c.closedAt)}`}
                {c.status === "applied" && c.zeroUnread && " · não lidos zerados"}
              </p>
              {c.notes && <p className="small mt-2 mb-0">{c.notes}</p>}
            </div>
            {c.reads.length > 0 && (
              <div className="box card p-3 mb-3">
                <h2 className="h6">Últimas leituras</h2>
                <ul className="list-unstyled small mb-0" aria-label="Últimas leituras">
                  {c.reads.map((r) => (
                    <li key={r.id} className="d-flex gap-2">
                      <span className="text-muted text-nowrap">{dateTime(r.readAt)}</span>
                      <span className="flex-grow-1">
                        {formatQty(r.qtyQ4)} × {r.productName}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      )}
      {c && applying && (
        <ApplyDialog
          count={c}
          onClose={() => setApplying(false)}
          onDone={() => {
            setApplying(false);
            refresh();
            qc.invalidateQueries({ queryKey: ["stock-balances", companyId] });
            qc.invalidateQueries({ queryKey: ["stock-movements", companyId] });
          }}
        />
      )}
      <ConfirmDialog
        open={!!resetting}
        title={resetting === "all" ? "Reiniciar contagem" : "Recontar produto"}
        message={
          resetting === "all"
            ? "Todas as leituras desta conferência são anuladas (ficam no histórico) e a contagem recomeça do zero."
            : `As leituras de ${resetting?.name ?? ""} são anuladas para recontar.`
        }
        confirmLabel="Anular leituras"
        onConfirm={() => {
          if (resetting) reset.mutate(resetting === "all" ? null : resetting.productId);
          setResetting(null);
        }}
        onCancel={() => setResetting(null)}
      />
      <ConfirmDialog
        open={canceling}
        title="Cancelar conferência"
        message="A conferência é encerrada sem mexer no estoque. As leituras ficam registradas."
        confirmLabel="Cancelar conferência"
        onConfirm={() => {
          cancel.mutate();
          setCanceling(false);
        }}
        onCancel={() => setCanceling(false)}
      />
    </>
  );
}

/** Aplicar: lança um balanço por produto lido (saldo do depósito = contado); opcionalmente zera os não lidos. */
function ApplyDialog({ count, onClose, onDone }: { count: StockCountView; onClose(): void; onDone(): void }) {
  const { api } = useSession();
  const toast = useToast();
  const [zeroUnread, setZero] = useState(false);
  const [idemKey] = useState(() => crypto.randomUUID());
  const apply = useMutation({
    mutationFn: () =>
      api<StockCountApplyResult>(`/stock/counts/${count.id}/apply`, {
        method: "POST",
        headers: { "Idempotency-Key": idemKey },
        body: JSON.stringify({ zeroUnread }),
      }),
    onSuccess: (r) => {
      toast(
        "success",
        r.adjusted === 0
          ? "Conferência aplicada: nenhum saldo precisou de ajuste"
          : `Conferência aplicada: ${r.adjusted} ${r.adjusted === 1 ? "produto ajustado" : "produtos ajustados"}`,
      );
      onDone();
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  return (
    <Modal
      open
      title={`Aplicar conferência nº ${count.number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={apply.isPending}
            onClick={() => apply.mutate()}
          >
            Aplicar
          </button>
        </>
      }
    >
      <p>
        O saldo de cada produto lido no depósito {count.warehouse.name} passa a ser o contado (lançamento de
        balanço).
        {count.summary.divergent > 0 &&
          ` ${count.summary.divergent} ${count.summary.divergent === 1 ? "produto tem" : "produtos têm"} diferença.`}
      </p>
      <div className="form-check">
        <input
          id="count-zero-unread"
          type="checkbox"
          className="form-check-input"
          checked={zeroUnread}
          disabled={count.summary.unreadProducts === 0}
          onChange={(e) => setZero(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="count-zero-unread">
          Zerar os {count.summary.unreadProducts} produtos com saldo que não foram lidos
        </label>
      </div>
    </Modal>
  );
}
